import React, { FC } from "react";
import { Box, Button, CircularProgress, FormControlLabel, InputAdornment, Switch, TextField, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { StepDots } from "../components/StepDots";
import { CustomFieldLabel } from "../../../components/fields/CustomFieldLabel/CustomFieldLabel";
import { ChannelQuantityPicker } from "../../../components/Orders/ChannelQuantityPicker";
import { ContentChannelOption } from "../../../components/Orders/ChannelMultiSelect";
import { InlineProductSelector } from "../../videoProduction/components/InlineProductSelector";
import InlineBrandSelector from "../../videoProduction/components/InlineBrandSelector";

interface ProjectFieldsStepProps {
    orderBrand: any;
    isRMNOrder: boolean;
    rmnBrandOptions: any[];
    channels: ContentChannelOption[];
    selectedChannels: ContentChannelOption[];
    onChannelsChange: (channels: ContentChannelOption[]) => void;
    channelsLoading: boolean;
    selectedProduct: any;
    setSelectedProduct: (p: any) => void;
    selectedProducts: any[];
    onProductsChange: (products: any[]) => void;
    selectedRmnBrandId: string | number | null;
    selectedProjectBrand: any;
    setSelectedProjectBrand: (b: any) => void;
    allowAddBrand: boolean;
    setFieldValue: (field: string, value: any) => void;
    // Internal-only manual price adjustment (staged as a string; "" = unset).
    showAdjustment: boolean;
    productionAdjustment: string;
    // Internal-only price lock (estimated → actual).
    priceLocked: boolean;
    isLoading: boolean;
    orderName: string;
    projectNumber: number;
    selectedBrandName?: string;
    onBack: () => void;
    onNext: () => void | Promise<void>;
}

export const ProjectFieldsStep: FC<ProjectFieldsStepProps> = ({
    orderBrand,
    isRMNOrder,
    rmnBrandOptions,
    channels,
    selectedChannels,
    onChannelsChange,
    channelsLoading,
    selectedProduct,
    setSelectedProduct,
    selectedProducts,
    onProductsChange,
    selectedRmnBrandId,
    selectedProjectBrand,
    setSelectedProjectBrand,
    allowAddBrand,
    setFieldValue,
    showAdjustment,
    productionAdjustment,
    priceLocked,
    isLoading,
    orderName,
    projectNumber,
    selectedBrandName,
    onBack,
    onNext,
}) => {
    const nextDisabled =
        (isRMNOrder && !selectedProjectBrand) ||
        (channels.length > 0 && selectedChannels.length === 0) ||
        (selectedProducts.length === 0 && !selectedProduct);

    // For RMN orders, products come from the selected supplier/child brand — never
    // the retailer. Until a supplier is chosen, there is no valid catalog to show.
    const effectiveBrandId = isRMNOrder
        ? selectedRmnBrandId || null
        : orderBrand?.id || null;
    const productCatalogName = isRMNOrder
        ? selectedProjectBrand?.name || null
        : orderBrand?.name || null;
    const productsDisabled = !orderBrand || (isRMNOrder && !selectedRmnBrandId);

    return (
        <>
            {/* Body */}
            <Box sx={{ flex: 1, overflowY: "auto", p: "20px 32px 20px" }}>
                <Typography sx={{ fontSize: 12, color: "var(--text-2)", mb: "4px", fontFamily: "CircularStd" }}>
                    Project {projectNumber}{orderName ? ` · ${orderName}` : ""}
                </Typography>
                <Box mb="6px">
                    <StepDots step={4} />
                </Box>
                <Box display="flex" alignItems="baseline" gap="10px" mb="6px">
                    <Typography
                        sx={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: "var(--text)",
                            letterSpacing: -0.4,
                            fontFamily: "CircularStd",
                        }}
                    >
                        Project Details
                    </Typography>
                    {isRMNOrder && orderBrand?.name && (
                        <Typography sx={{ fontSize: 11.5, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                            for{" "}
                            <Box component="strong" sx={{ color: "var(--text)", fontWeight: 600 }}>
                                {orderBrand.name}
                            </Box>
                        </Typography>
                    )}
                </Box>
                <Typography sx={{ fontSize: 13, color: "var(--text-2)", mb: "14px", fontFamily: "CircularStd" }}>
                    {isRMNOrder
                        ? "Pick the supplier brand, then where it runs and which products are featured."
                        : "Where will this video run, and which products is it featuring?"}
                </Typography>

                <Box display="flex" flexDirection="column" gap="14px">
                    {/* RMN Brand selector */}
                    {isRMNOrder && (
                        <div>
                            <Box display="flex" justifyContent="space-between" alignItems="baseline" mb="6px">
                                <CustomFieldLabel required>Brand</CustomFieldLabel>
                                {rmnBrandOptions.length > 0 && (
                                    <Typography sx={{ fontSize: 11, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                                        Supplier on {orderBrand?.name} · {rmnBrandOptions.length} available
                                    </Typography>
                                )}
                            </Box>
                            <InlineBrandSelector
                                setFieldValue={(field: string, value: any) => {
                                    if (field === "brand" || field === "brands") return;
                                    setFieldValue(field, value);
                                }}
                                isRMN={false}
                                showRmnToggle={false}
                                selectedBrand={selectedProjectBrand}
                                setSelectedBrand={(brand: any) => {
                                    setSelectedProjectBrand(brand);
                                    setFieldValue("new_rmn_brand", brand?.id || null);
                                    setSelectedProduct(null);
                                    onProductsChange([]);
                                    setFieldValue("new_product_name", "");
                                }}
                                disabled={false}
                                filterRmn={false}
                                allowAdd={allowAddBrand}
                                // Restrict the supplier picker to the RMN's
                                // associated brands (the "{n} available" set)
                                // instead of a free search over every brand.
                                scopedOptions={rmnBrandOptions}
                            />
                        </div>
                    )}

                    {/* Channels */}
                    <div>
                        <Box display="flex" justifyContent="space-between" alignItems="baseline" mb="8px">
                            <CustomFieldLabel required={channels.length > 0}>Channels</CustomFieldLabel>
                            {!channelsLoading && channels.length > 0 && (
                                <Typography sx={{ fontSize: 11, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                                    {selectedChannels.length} selected · tap to add, − to remove
                                </Typography>
                            )}
                        </Box>
                        <ChannelQuantityPicker
                            channels={channels}
                            selectedChannels={selectedChannels}
                            onChange={onChannelsChange}
                            loading={channelsLoading}
                        />
                        {channels.length > 0 && selectedChannels.length === 0 && (
                            <Typography sx={{ fontSize: 11.5, color: "#E07A5F", mt: "3px", fontFamily: "CircularStd" }}>
                                Select at least one channel to continue
                            </Typography>
                        )}
                    </div>

                    {/* Products */}
                    <div>
                        <Box display="flex" justifyContent="space-between" alignItems="baseline" mb="6px">
                            <CustomFieldLabel required>Products</CustomFieldLabel>
                            {productCatalogName ? (
                                <Typography sx={{ fontSize: 11, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                                    from {productCatalogName} catalog
                                </Typography>
                            ) : isRMNOrder ? (
                                <Typography sx={{ fontSize: 11, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                                    Select a brand first
                                </Typography>
                            ) : null}
                        </Box>
                        <InlineProductSelector
                            setFieldValue={setFieldValue}
                            selectedProduct={selectedProduct}
                            setSelectedProduct={setSelectedProduct}
                            multiple={true}
                            selectedProducts={selectedProducts}
                            onProductsChange={onProductsChange}
                            selectedBrandId={effectiveBrandId}
                            selectedBrandName={selectedBrandName}
                            disabled={productsDisabled}
                        />
                    </div>

                    {/* Production adjustment — internal-only manual price tweak */}
                    {showAdjustment && (
                        <div>
                            <Box display="flex" justifyContent="space-between" alignItems="baseline" mb="6px">
                                <CustomFieldLabel>Production adjustment</CustomFieldLabel>
                                <Typography sx={{ fontSize: 11, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                                    internal · applied after discounts
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                placeholder="0.00"
                                value={productionAdjustment}
                                onChange={(e) =>
                                    setFieldValue("new_production_adjustment", e.target.value)
                                }
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                    ),
                                }}
                                helperText="Signed — use a negative value to reduce the price."
                            />
                        </div>
                    )}

                    {/* Price lock — internal-only: estimated → actual (freezes pricing) */}
                    {showAdjustment && (
                        <div>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={priceLocked}
                                        onChange={(e) =>
                                            setFieldValue("new_price_locked", e.target.checked)
                                        }
                                        size="small"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)", fontFamily: "CircularStd" }}>
                                            Lock price (actual)
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                                            internal · freezes the price; it stops recalculating
                                        </Typography>
                                    </Box>
                                }
                            />
                        </div>
                    )}
                </Box>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "14px 28px",
                    borderTop: "1px solid var(--border)",
                    background: "var(--surface)",
                    flexShrink: 0,
                }}
            >
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{ textTransform: "capitalize", fontFamily: "CircularStd", color: "var(--text)" }}
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={nextDisabled || isLoading}
                    endIcon={
                        isLoading
                            ? <CircularProgress size={14} sx={{ color: "#fff" }} />
                            : <ArrowForwardIcon />
                    }
                    onClick={onNext}
                    sx={{
                        textTransform: "capitalize",
                        fontFamily: "CircularStd",
                        borderRadius: "24px",
                        px: 3,
                    }}
                >
                    Next
                </Button>
            </Box>
        </>
    );
};
